import { Link } from "react-router-dom";
import { useEffect } from "react";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";

const articleSchema = {
  "@context": "https://schema.org",
  "@type": "Article",
  "headline": "Kakobuy wysyłka do Polski – Koszty, czas i metody dostawy (2026)",
  "description": "Kompletny przewodnik po wysyłce Kakobuy do Polski. Porównanie metod dostawy EUB, EMS, DHL, FedEx. Sprawdź koszty, czas transportu i jak zaoszczędzić na przesyłce dzięki Rehearsal Shipping.",
  "author": { "@type": "Organization", "name": "Kakobuy Polska" },
  "datePublished": "2026-07-21",
  "dateModified": "2026-07-22",
  "inLanguage": "pl",
  "publisher": { "@type": "Organization", "name": "Kakobuy Polska", "url": "https://kakobuypolska.boats" }
};

const faqSchema = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  "mainEntity": [
    {
      "@type": "Question",
      "name": "Ile kosztuje wysyłka Kakobuy do Polski?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Koszt wysyłki Kakobuy do Polski zależy od wagi paczki, wymiarów i wybranej metody dostawy. Ceny zaczynają się od około 30-50 zł za małe paczki ekonomiczną metodą (EUB) i mogą sięgać 200+ zł za większe przesyłki ekspresowe (DHL, FedEx)."
      }
    },
    {
      "@type": "Question",
      "name": "Jak długo trwa wysyłka Kakobuy do Polski?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Czas wysyłki Kakobuy do Polski wynosi od 5 do 30 dni roboczych, w zależności od metody: ekspresowa (DHL, FedEx) 5-10 dni, standardowa (EMS, DPD) 10-20 dni, ekonomiczna (EUB, China Post) 15-30 dni."
      }
    },
    {
      "@type": "Question",
      "name": "Co to jest Rehearsal Shipping w Kakobuy?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Rehearsal Shipping to usługa Kakobuy, która pozwala dokładnie poznać wagę i wymiary paczki przed wysyłką. Pracownicy fizycznie pakują i ważą produkty, a następnie podają dokładny koszt wysyłki. Pozwala to uniknąć przepłacania za szacunkową wagę i często obniża koszty transportu."
      }
    }
  ]
};

const breadcrumbSchema = {
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  "itemListElement": [
    { "@type": "ListItem", "position": 1, "name": "Strona Główna", "item": "https://kakobuypolska.boats/" },
    { "@type": "ListItem", "position": 2, "name": "Wysyłka Kakobuy do Polski", "item": "https://kakobuypolska.boats/artykul/kakobuy-wysylka-polska" }
  ]
};

export default function ShippingArticle() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />
      <main className="pt-20 md:pt-24">
        <div className="w-full max-w-4xl mx-auto px-4 md:px-6 py-4">
          <nav className="flex items-center gap-2 text-sm text-foreground-500">
            <Link to="/" className="hover:text-primary-500 transition-colors whitespace-nowrap">Strona Główna</Link>
            <i className="ri-arrow-right-s-line text-xs"></i>
            <span className="text-foreground-700 font-medium whitespace-nowrap">Wysyłka Kakobuy do Polski</span>
          </nav>
        </div>

        <header className="w-full max-w-4xl mx-auto px-4 md:px-6 pb-8">
          <div className="flex flex-wrap items-center gap-3 mb-4">
            <span className="inline-flex items-center gap-1 px-3 py-1 bg-secondary-100 text-secondary-700 text-xs font-semibold rounded-full whitespace-nowrap">
              <i className="ri-ship-line"></i>Wysyłka
            </span>
            <span className="inline-flex items-center gap-1 px-3 py-1 bg-background-100 text-foreground-600 text-xs font-semibold rounded-full whitespace-nowrap">
              <i className="ri-time-line"></i>Aktualizacja: 22.07.2026
            </span>
          </div>
          <h1 className="font-heading text-2xl md:text-4xl lg:text-5xl font-bold text-foreground-900 leading-tight mb-4">
            Kakobuy wysyłka do Polski – Koszty, czas i metody dostawy (2026)
          </h1>
          <p className="text-foreground-600 text-sm md:text-lg leading-relaxed max-w-3xl">
            Wszystko o wysyłce Kakobuy do Polski: porównanie metod EUB, EMS, DHL i FedEx, koszty transportu, czas dostawy, Rehearsal Shipping oraz praktyczne porady jak zaoszczędzić na przesyłce.
          </p>
        </header>

        <div className="w-full max-w-4xl mx-auto px-4 md:px-6 pb-10">
          <div className="relative w-full h-[280px] md:h-[420px] rounded-2xl overflow-hidden">
            <img
              src="https://readdy.ai/api/search-image?query=Modern%20minimalist%20logistics%20and%20shipping%20concept%20with%20cardboard%20parcels%20on%20warm%20cream%20desk%2C%20soft%20natural%20daylight%2C%20clean%20aesthetic%2C%20neutral%20warm%20tones%2C%20elegant%20editorial%20photography%2C%20subtle%20shadows%2C%20European%20style%2C%20high%20detail%2C%20delivery%20boxes%20composition&width=1600&height=900&seq=shipping-article-hero&orientation=landscape"
              alt="Wysyłka Kakobuy do Polski - poradnik"
              className="w-full h-full object-cover object-top"
            />
          </div>
        </div>

        <article className="w-full max-w-4xl mx-auto px-4 md:px-6 pb-16">
          <div className="prose prose-base md:prose-lg max-w-none text-foreground-700 space-y-6 leading-relaxed">

            <section>
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mt-0">
                Kakobuy shipping Poland — wszystko, co musisz wiedzieć
              </h2>
              <p>
                <strong>Kakobuy shipping Poland</strong> to temat, który interesuje każdego polskiego kupującego korzystającego z chińskich platform zakupowych. Koszty i czas wysyłki często decydują o opłacalności całego zamówienia. W tym kompleksowym przewodniku wyjaśniamy wszystkie dostępne metody dostawy, ich ceny, czas transportu oraz sprawdzone sposoby na obniżenie kosztów przesyłki.
              </p>
              <p>
                Kakobuy oferuje kilka opcji wysyłki — od ekonomicznych (dla cierpliwych i oszczędnych) po ekspresowe (dla tych, którzy chcą otrzymać paczkę jak najszybciej). Wybór odpowiedniej metody może zaoszczędzić Ci nawet kilkaset złotych na większych zamówieniach.
              </p>
            </section>

            <section>
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900">
                Dostępne metody wysyłki Kakobuy do Polski
              </h2>
              <p>Kakobuy współpracuje z kilkoma przewoźnikami oferującymi różne metody dostawy do Polski:</p>
              <ul className="space-y-3">
                <li>
                  <strong>EUB / China Post (ekonomiczna):</strong> Najtańsza metoda, idealna dla małych paczek do 2 kg. Czas dostawy: 15-30 dni roboczych. Koszt: od 30-50 zł. Dobra opcja, gdy nie spieszysz się z odbiorem.
                </li>
                <li>
                  <strong>EMS / DPD (standardowa):</strong> Średnia cena i czas. Czas dostawy: 10-20 dni roboczych. Koszt: od 60-120 zł w zależności od wagi. Dobry balans między ceną a szybkością.
                </li>
                <li>
                  <strong>DHL / FedEx (ekspresowa):</strong> Najszybsza opcja. Czas dostawy: 5-10 dni roboczych. Koszt: od 100-250+ zł. Idealna dla pilnych zamówień i wartościowych produktów.
                </li>
                <li>
                  <strong>Linia morska (ekonomiczna duża):</strong> Dla bardzo dużych zamówień. Czas: 30-60 dni. Najtańsza dla ciężkich paczek, ale wymaga cierpliwości.
                </li>
              </ul>

              <div className="overflow-x-auto not-prose mt-6">
                <table className="w-full border-collapse rounded-xl overflow-hidden border border-background-200/70">
                  <thead>
                    <tr className="bg-background-100">
                      <th className="text-left py-3 px-4 font-heading font-semibold text-foreground-800 text-sm">Metoda</th>
                      <th className="text-center py-3 px-4 font-heading font-semibold text-foreground-600 text-sm">Czas</th>
                      <th className="text-center py-3 px-4 font-heading font-semibold text-foreground-600 text-sm">Koszt (od)</th>
                      <th className="text-center py-3 px-4 font-heading font-semibold text-foreground-600 text-sm">Śledzenie</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[
                      ["EUB / China Post", "15-30 dni", "30-50 zł", "Podstawowe"],
                      ["EMS / DPD", "10-20 dni", "60-120 zł", "Pełne"],
                      ["DHL / FedEx", "5-10 dni", "100-250 zł", "Pełne + SMS"],
                      ["Linia morska", "30-60 dni", "50-150 zł", "Ograniczone"],
                    ].map((row, i) => (
                      <tr key={i} className="border-t border-background-200/70 hover:bg-background-50/50 transition-colors">
                        <td className="py-3 px-4 font-medium text-foreground-800 text-sm">{row[0]}</td>
                        <td className="py-3 px-4 text-center text-foreground-500 text-sm">{row[1]}</td>
                        <td className="py-3 px-4 text-center text-primary-600 font-semibold text-sm">{row[2]}</td>
                        <td className="py-3 px-4 text-center text-foreground-500 text-sm">{row[3]}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>

            <section>
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900">
                Rehearsal Shipping — sposób na niższe koszty
              </h2>
              <p>
                <strong>Rehearsal Shipping</strong> to jedna z najbardziej przydatnych funkcji Kakobuy, o której wielu początkujących nie wie. Polega ona na fizycznym spakowaniu i zważeniu Twoich produktów przed wysyłką, aby dokładnie określić koszt transportu.
              </p>
              <p>Dlaczego to ważne? System Kakobuy szacuje wagę paczki na podstawie danych od sprzedawcy. Te szacunki często są zawyżone — co oznacza, że płacisz za wagę, której Twoja paczka faktycznie nie ma. Rehearsal Shipping eliminuje ten problem:</p>
              <ul className="space-y-2">
                <li>Pracownicy Kakobuy fizycznie ważą i mierzą Twoje produkty</li>
                <li>Otrzymujesz dokładny koszt wysyłki przed jej opłaceniem</li>
                <li>Często okazuje się, że rzeczywisty koszt jest niższy niż szacowany</li>
                <li>Koszt usługi to zazwyczaj tylko kilka złotych</li>
              </ul>
            </section>

            <section>
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900">
                Jak zaoszczędzić na wysyłce Kakobuy do Polski?
              </h2>
              <p>Oto sprawdzone strategie obniżania kosztów wysyłki:</p>
              <ol className="space-y-2">
                <li><strong>Łącz produkty w jednej paczce:</strong> Wysyłka kilku produktów razem jest tańsza niż wysyłanie każdego osobno.</li>
                <li><strong>Usuwaj oryginalne opakowania:</strong> Pudełka po butach i zbędne opakowania znacząco zwiększają wagę i objętość paczki.</li>
                <li><strong>Korzystaj z Rehearsal Shipping:</strong> Zapłacisz tylko za rzeczywistą wagę, nie szacunkową.</li>
                <li><strong>Wybieraj ekonomiczne metody dla niepilnych zamówień:</strong> EUB jest znacznie tańszy niż DHL.</li>
                <li><strong>Zamawiaj w grupie:</strong> Podziel koszty wysyłki ze znajomymi — im większa paczka, tym niższy koszt za kilogram.</li>
              </ol>
            </section>

            <section>
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900">
                Cło i podatki przy wysyłce do Polski
              </h2>
              <p>
                Przy zamawianiu z Chin do Polski należy pamiętać o potencjalnych opłatach celnych. Od 1 lipca 2021 roku wszystkie przesyłki spoza UE podlegają opodatkowaniu VAT (23% w Polsce), niezależnie od wartości. Dodatkowo, dla przesyłek o wartości powyżej 150 EUR naliczane jest cło.
              </p>
              <p>
                Kakobuy oferuje opcję <strong>deklarowania niższej wartości paczki</strong> na etykiecie wysyłkowej, co może pomóc w uniknięciu dodatkowych opłat. Pamiętaj jednak, że jest to praktyka obarczona pewnym ryzykiem — w przypadku kontroli celnej mogą zostać naliczone dodatkowe opłaty.
              </p>
            </section>

            <section>
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900">
                FAQ — Wysyłka Kakobuy do Polski
              </h2>
              <div className="space-y-5 not-prose">
                {[
                  {
                    q: "Czy mogę śledzić paczkę Kakobuy?",
                    a: "Tak, wszystkie metody wysyłki (z wyjątkiem linii morskiej) oferują śledzenie przesyłki. Numer trackingowy otrzymasz po opłaceniu wysyłki w panelu Kakobuy."
                  },
                  {
                    q: "Co zrobić, jeśli paczka zaginie?",
                    a: "Skontaktuj się z obsługą Kakobuy. Jeśli wykupiłeś ubezpieczenie, otrzymasz pełny zwrot. Bez ubezpieczenia otrzymasz częściową rekompensatę zgodnie z regulaminem."
                  },
                  {
                    q: "Czy mogę zmienić adres dostawy po nadaniu paczki?",
                    a: "W większości przypadków nie jest to możliwe. Upewnij się, że adres w panelu Kakobuy jest poprawny przed opłaceniem wysyłki."
                  },
                  {
                    q: "Jaka jest maksymalna waga paczki?",
                    a: "Zależy od metody. EUB: do 2 kg. EMS/DPD: do 20-30 kg. DHL/FedEx: do 30-70 kg. Dla bardzo dużych zamówień dostępna jest wysyłka morska."
                  },
                ].map((faq, i) => (
                  <details key={i} className="group bg-background-100 rounded-xl border border-background-200/70 overflow-hidden">
                    <summary className="flex items-center justify-between px-5 py-4 cursor-pointer font-heading font-semibold text-foreground-800 text-sm md:text-base list-none">
                      {faq.q}
                      <i className="ri-arrow-down-s-line text-foreground-500 text-lg group-open:rotate-180 transition-transform duration-200"></i>
                    </summary>
                    <div className="px-5 pb-4 text-foreground-600 text-sm leading-relaxed">{faq.a}</div>
                  </details>
                ))}
              </div>
            </section>

            <section className="not-prose bg-primary-500/10 rounded-2xl p-6 md:p-8 border border-primary-200/50 mt-10">
              <h2 className="font-heading text-xl font-bold text-foreground-900 mb-3">
                Sprawdź produkty i zamów z dostawą do Polski
              </h2>
              <p className="text-foreground-600 text-sm mb-5">
                Przeglądaj Kakobuy Spreadsheet i wybierz najlepszą metodę wysyłki.
              </p>
              <div className="flex flex-col sm:flex-row gap-3">
                <a href="https://kaspreadsheet.com" target="_blank" rel="noopener nofollow" className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-primary-500 text-background-50 dark:text-foreground-950 font-heading font-semibold text-sm rounded-full hover:bg-primary-600 transition-all duration-300 whitespace-nowrap cursor-pointer">
                  Kakobuy Spreadsheet<i className="ri-arrow-right-up-line"></i>
                </a>
                <Link to="/" className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-background-50 border-2 border-foreground-200 text-foreground-800 font-heading font-semibold text-sm rounded-full hover:border-primary-400 hover:text-primary-600 transition-all duration-300 whitespace-nowrap">
                  <i className="ri-home-line"></i>Powrót na stronę główną
                </Link>
              </div>
            </section>
          </div>
        </article>
      </main>
      <Footer />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(articleSchema) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }} />
    </div>
  );
}